'use client';

import cn from 'classnames';
import * as SelectPrimitive from '@radix-ui/react-select';
import { useRef, useState, useEffect, useMemo } from 'react';
import { Field, FieldDescription, FieldError, FieldLabel, Text } from '@/shared/ui/core';
import { CheckIcon, CaretDownIcon, MagnifyingGlassIcon } from '@phosphor-icons/react/dist/ssr';
import s from '@/shared/styles/controls/dropdown-input.module.scss';
import { SelectProps } from '../model/select.types';
import styles from './select.module.scss';


export function Select ({
    value,
    defaultValue,
    onValueChange,
    placeholder = 'Select option',
    label,
    description,
    error,
    state = 'default',
    options,
    disabled,
    className,
    triggerClassName,
    required,
    searchable,
    searchPlaceholder,
}: SelectProps) {
    const [ open, setOpen ]                   = useState(false);
    const [ search, setSearch ]               = useState('');
    const [ internalValue, setInternalValue ] = useState(defaultValue ?? value);
    const searchRef                           = useRef<HTMLInputElement>(null);
    const viewportRef                         = useRef<HTMLDivElement>(null);

    const isError       = state === 'error' || Boolean(error);
    const isSuccess     = state === 'success';
    const isWarning     = state === 'warning';
    const isDefault     = state === 'default';
    const textClassname = cn({
        [s.default]    : isDefault,
        [s.destructive]: isError,
        [s.success]    : isSuccess,
        [s.warning]    : isWarning,
        [s.disabled]   : disabled,
    });

    const handleValueChange = (val: string) => {
        setInternalValue(val);
        onValueChange?.(val);
    };

    const filteredOptions = useMemo(() => {
        if (!searchable || !search.trim()) return options;
        const query = search.toLowerCase();
        return options.filter((o) => {
            const label = typeof o.label === 'string' ? o.label : '';
            return label.toLowerCase().includes(query) || o.value.toLowerCase().includes(query);
        });
    }, [ options, search, searchable ]);

    const handleOpenChange = (next: boolean) => {
        setOpen(next);
        if (next) {
            setSearch('');
            setTimeout(() => searchRef.current?.focus(), 0);
        }
    };

    useEffect(() => {
        if (!open || !value) return;
        const timer = setTimeout(() => {
            const el = viewportRef.current?.querySelector<HTMLElement>(
                `[data-value="${ CSS.escape(value) }"]`,
            );
            el?.scrollIntoView({ block: 'nearest' });
        }, 50);
        return () => clearTimeout(timer);
    }, [ open, value ]);

    return (
        <Field className={ cn(s.root, className) }>
            <SelectPrimitive.Root
                open={ open }
                onOpenChange={ handleOpenChange }
                value={ internalValue }
                onValueChange={ handleValueChange }
                disabled={ disabled }
            >
                <SelectPrimitive.Trigger
                    className={ cn(s.trigger, textClassname, triggerClassName) }
                    aria-invalid={ isError }
                >
                    <div className={ s.trigger_inner }>
                        {
                            label &&
                            <FieldLabel
                                className={ textClassname }
                                required={ required }
                            >
                                { label }
                            </FieldLabel>
                        }

                        <div className={ s.value_wrapper }>
                            <SelectPrimitive.Value
                                placeholder={
                                    <span className={ s.placeholder }>
                                        { placeholder }
                                    </span>
                                }
                            >
                                <Text
                                    size='xl'
                                    weight='semibold'
                                    lineHeight={ 1 }
                                    className={ s.value }
                                >
                                    { internalValue
                                        ? options.find(o => o.value === internalValue)?.label
                                        : undefined
                                    }
                                </Text>
                            </SelectPrimitive.Value>
                        </div>
                    </div>

                    <SelectPrimitive.Icon asChild>
                        <CaretDownIcon
                            size={ 20 }
                            className={ s.chevron }
                        />
                    </SelectPrimitive.Icon>
                </SelectPrimitive.Trigger>

                <SelectPrimitive.Portal>
                    <SelectPrimitive.Content
                        position="popper"
                        sideOffset={ 6 }
                        onCloseAutoFocus={ (e) => e.preventDefault() }
                        className={ s.content }
                    >
                        {
                            searchable &&
                            <div className={ styles.search_wrapper }>

                                <input
                                    ref={ searchRef }
                                    value={ search }
                                    onChange={ (e) => setSearch(e.target.value) }
                                    placeholder={ searchPlaceholder ?? placeholder }
                                    className={ styles.search_input }
                                    onPointerDown={ (e) => e.stopPropagation() }
                                    onKeyDown={ (e) => {
                                        if (e.key === ' ') e.stopPropagation();
                                        if (e.key === 'Escape') setOpen(false);
                                    } }
                                    autoComplete="one-time-code"
                                />

                                <MagnifyingGlassIcon
                                    size={ 20 }
                                    className={ styles.search_icon }
                                />
                            </div>
                        }

                        <SelectPrimitive.Viewport
                            ref={ viewportRef }
                            className={ styles.viewport }
                        >
                            {
                                filteredOptions.length === 0
                                    ? (
                                        <div className={ styles.empty }>
                                            Nothing found
                                        </div>
                                    )
                                    : filteredOptions.map((option) => (
                                        <SelectPrimitive.Item
                                            key={ option.value }
                                            value={ option.value }
                                            data-value={ option.value }
                                            className={ styles.item }
                                        >
                                            <span className={ styles.item_label }>
                                                { option.label }
                                            </span>

                                            <span className={ styles.item_indicator }>
                                                <SelectPrimitive.ItemIndicator>
                                                    <CheckIcon size={ 12 }/>
                                                </SelectPrimitive.ItemIndicator>
                                            </span>
                                        </SelectPrimitive.Item>
                                    ))
                            }
                        </SelectPrimitive.Viewport>
                    </SelectPrimitive.Content>
                </SelectPrimitive.Portal>
            </SelectPrimitive.Root>

            {
                isError &&
                error &&
                <FieldError className={ textClassname }>
                    { error }
                </FieldError>
            }

            {
                !isError &&
                description &&
                <FieldDescription className={ textClassname }>
                    { description }
                </FieldDescription>
            }
        </Field>
    );
}
